import { useCallback, useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import PageHeader from 'src/core/ui/PageHeader';
import Footer from 'src/core/ui/helpers/Footer';
import ContentCardsContainer, { CardType, ContentCard } from 'src/core/ui/ContentCard';
import { CustomDataGrid } from 'src/core/tables/CustomDataGrid';
import { generateTablePageButtons } from 'src/core/tables/generateTablePageButtons';
import { useFullfillmentOrderRowsTable, useFullfillmentOrdersTable } from 'src/pages/warehouses/fulfillment/useFullfillmentOrdersTable';
import DetailPanelContainer from 'src/core/tables/helpers/DetailPanelContainer';
import CustomButton from 'src/core/ui/CustomButton';
import useRequest from 'src/core/api/useRequest';
import { dispatchFullfillmentOrder } from 'src/core/api/apiRequests';

export const FulfillmentOrdersTablePage = () => {
    const [refreshKey, setRefreshKey] = useState(0);
    const [columns, tableSettings] = useFullfillmentOrdersTable(refreshKey);
    const refreshOrders = useCallback(() => setRefreshKey(key => key + 1), []);
    const renderDetailPanel = useCallback(params => <DetailPanelContent key={params.id} orderId={String(params.id)} onDispatched={refreshOrders} />, [refreshOrders]);
    return (
        <>
            <PageHeader {...{
                title: 'Fulfillment Orders',
                subTitle: 'Track orders, their products and the warehouses they ship from',
                components: [
                    ...generateTablePageButtons(tableSettings),
                ],
            }} />
            <ContentCardsContainer>
                <ContentCard {...{ cardTypes: [CardType.noHeader], contentPadding: 0 }}>
                    <CustomDataGrid columns={columns} tableSettings={tableSettings} getDetailPanelContent={renderDetailPanel} />
                </ContentCard>
            </ContentCardsContainer>
            <Footer />
        </>
    );
};
export default FulfillmentOrdersTablePage;

const DetailPanelContent = ({ orderId, onDispatched }: { orderId: string; onDispatched: () => void }) => {
    const [refreshKey, setRefreshKey] = useState(0);
    const [columns, tableSettings, createdDispatchCommandId] = useFullfillmentOrderRowsTable(orderId, refreshKey);
    const [, setRequest, , loading, status] = useRequest({ logLevel: ['error', 'success'] });
    const pending = useRef(false);
    const [dispatching, setDispatching] = useState(false);
    useEffect(() => {
        const succeeded = status === 'success' || status === 204;
        const failed = status === 'error' || status === 'aborted' || (typeof status === 'number' && status >= 400);
        if (!pending.current || loading || (!succeeded && !failed)) return;
        pending.current = false;
        setDispatching(false);
        if (succeeded) {
            setRefreshKey(key => key + 1);
            onDispatched();
        }
    }, [status, loading, onDispatched]);
    const dispatchOrder = () => {
        if (pending.current || createdDispatchCommandId == null || tableSettings.loading) return;
        pending.current = true;
        setDispatching(true);
        setRequest(dispatchFullfillmentOrder(orderId, createdDispatchCommandId));
    };
    const detailStatus = String(tableSettings.status ?? 'idle');
    const disabledReason = dispatching ? 'This dispatch command is already being sent.' : tableSettings.loading || detailStatus === 'idle' ? 'Loading order details. Wait before dispatching.' : detailStatus !== 'success' ? 'Order details are unavailable. Reopen the order details to try again.' : createdDispatchCommandId == null ? 'No dispatch command in Created status is available for this order.' : '';
    return (
        <DetailPanelContainer>
            <Box sx={{ px: 2, py: 1, width: 'fit-content', maxWidth: '100%' }}>
                <CustomButton title="Dispatch Order Manually" tooltipText={disabledReason} disabled={Boolean(disabledReason)} isSubmitting={dispatching} onClick={dispatchOrder} />
            </Box>
            <CustomDataGrid columns={columns} tableSettings={tableSettings} />
        </DetailPanelContainer>
    );
};
